/* Theme custom file */

import { prefersReducedMotion } from '@theme/utilities';

const HASH_ALIASES = ['a-venir', 'avenir', 'upcoming', 'ventes-a-venir'];

/** Hash written to the URL when the upcoming tab is activated on the homepage. */
const UPCOMING_HASH = 'a-venir';

/**
 * Date tabs, homepage only. Each rule gets the sale window the section renders
 * on active cards (data-starts-at, data-ends-at) and the moment of the visit,
 * all in epoch seconds. Evaluated in the browser because Shopify caches the
 * HTML, so the server cannot know what day the visitor is on. Calendar days
 * are the store's, whatever the visitor's time zone.
 */
const STORE_TIME_ZONE = 'Europe/Paris';
const ONE_DAY = 86400;
const storeDay = new Intl.DateTimeFormat('en-CA', {
  timeZone: STORE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** @type {Map<string, (start: number, end: number, now: number) => boolean>} */
const DATE_TAB_RULES = new Map([
  // New today: already open, and opened on the current calendar day.
  ['new_today', (start, end, now) => start <= now && storeDay.format(start * 1000) === storeDay.format(now * 1000)],
  // Last day: inside the final 24 hours of a sale that ran longer than that.
  ['last_day', (start, end, now) => now >= end - ONE_DAY && now < end && end - ONE_DAY >= start],
]);

class OperationGrid extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {number} */
  #pageSize;

  /** @type {boolean} */
  #isHomepage;

  /** @type {number} 0 = continuous infinite scroll; N = pause after every N revealed batches */
  #pagesBeforePause = 0;

  /** @type {IntersectionObserver|null} */
  #observer = null;

  /** @type {HTMLElement|null} */
  #sentinel = null;

  /** @type {HTMLElement|null} The homepage hero, a sibling of this element. */
  #hero = null;

  /** @type {number} */
  #activeTabIndex = 0;

  /** @type {Map<number, number>} tabIndex -> visible count */
  #visibleCounts = new Map();

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {HTMLElement|null} */
  #grid = null;

  /** @type {HTMLElement|null} */
  #emptyState = null;

  /** @type {HTMLElement|null} */
  #cta = null;

  /** @type {HTMLElement|null} */
  #ctaWrapper = null;

  /** @type {HTMLElement|null} */
  #tabBar = null;

  /** @type {HTMLElement|null} */
  #indicator = null;

  /** @type {ResizeObserver|null} */
  #layoutObserver = null;

  /** @type {{link: HTMLAnchorElement, index: number}[]} Header links adopted as tab controls */
  #headerLinks = [];

  connectedCallback() {
    this.#pageSize = parseInt(this.dataset.pageSize, 10) || 20;
    this.#isHomepage = this.dataset.isHomepage === 'true';
    this.#pagesBeforePause = parseInt(this.dataset.pagesBeforePause, 10) || 0;

    const { signal } = this.#controller;

    // Cache refs
    this.#tabs = [...this.querySelectorAll('.operation-grid__tab')];
    this.#cards = [...this.querySelectorAll('.operation-grid__card')];
    this.#grid = this.querySelector('.operation-grid__grid');
    this.#emptyState = this.querySelector('.operation-grid__empty');
    this.#ctaWrapper = this.querySelector('.operation-grid__cta');
    this.#cta = this.#ctaWrapper?.querySelector('[data-load-more]') ?? null;
    this.#sentinel = this.querySelector('.operation-grid__sentinel');
    this.#tabBar = this.querySelector('.operation-grid__tabs');
    // Rendered ahead of <operation-grid> in the section rather than inside it.
    this.#hero = document.querySelector('.operation-grid__hero');
    this.#indicator = this.querySelector('.operation-grid__tab-indicator');

    // Tab bar
    if (this.#tabBar) {
      this.#tabBar.addEventListener('click', this.#handleTabClick, { signal });
      this.#tabBar.addEventListener('keydown', this.#handleTabKeydown, { signal });
    }

    // CTA load more
    if (this.#cta) {
      this.#cta.addEventListener('click', this.#handleLoadMore, { signal });
    }

    // Upgrade tabs on homepage
    if (this.#isHomepage) {
      this.#hideEmptyTabs();
      this.#upgradeTabsToARIA();
      for (const [i, tab] of this.#tabs.entries()) {
        tab.setAttribute('aria-selected', String(i === 0));
      }

      const matching = this.#tabs[0] ? this.#getMatchingCards(this.#tabs[0]) : [];
      const expectedVisible = matching.slice(0, this.#pageSize);
      const actuallyVisible = [...this.querySelectorAll('.operation-grid__card[data-visible]')];

      // The common case: server-rendered state already matches tab 0's
      // expected initial state (the first pageSize active cards visible,
      // nothing else), so there's nothing to correct. Only fall back to
      // #activateTab's hide-then-reshow cycle -- which causes a layout
      // shift, hence avoided -- when that's NOT true (e.g. active-operation
      // count below pageSize, so the shared grid_card_index counter also
      // server-marked some upcoming cards visible). Unconditionally running
      // that cycle on every load, even when redundant, forced an avoidable
      // reflow right around when the homepage's LCP element paints.
      const alreadyCorrect =
        actuallyVisible.length === expectedVisible.length &&
        expectedVisible.every((card, i) => actuallyVisible[i] === card);

      if (!alreadyCorrect) {
        this.#hideAllCards();
        this.#showCards(matching, this.#pageSize);
      }
      this.#visibleCounts.set(0, expectedVisible.length);

      const isEmpty = matching.length === 0;
      if (this.#grid) this.#grid.hidden = isEmpty;
      if (this.#emptyState) this.#emptyState.hidden = !isEmpty;

      this.#syncScroll();
      this.#adoptHeaderLinks();
      window.addEventListener('popstate', this.#handlePopState, { signal });
    } else {
      // Seed visible count from server-rendered cards, then arm infinite scroll for the rest
      const initialVisible = this.querySelectorAll('.operation-grid__card[data-visible]').length;
      this.#visibleCounts.set(0, initialVisible);
      this.#syncScroll();
    }

    // Hash navigation
    this.#handleHashNavigation();

    // Announce the initial tab once, unless the hash navigation already activated one.
    if (this.#isHomepage && !this.dataset.activeTab) this.#announceTab(null);

    // Indicator, once the tab bar has its final layout
    this.#armIndicator();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#layoutObserver?.disconnect();
  }

  // ── Tab upgrade (homepage only) ──

  #upgradeTabsToARIA() {
    if (!this.#tabBar) return;

    this.#tabBar.setAttribute('role', 'tablist');

    for (const [i, tab] of this.#tabs.entries()) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('tabindex', i === 0 ? '0' : '-1');
      tab.removeAttribute('aria-current');
    }
  }

  /**
   * A tab with no sale to show is dropped from the bar, and from the keyboard
   * and indicator handling with it. This covers the date tabs and the category
   * tabs that opt in with hide_when_empty. The server renders them hidden;
   * this is what reveals the ones that have something, as of page load. The
   * theme editor keeps every tab in view so the blocks stay reachable.
   */
  #hideEmptyTabs() {
    const designMode = Boolean(window.Shopify?.designMode);
    for (const tab of this.#tabs) {
      const { tabType } = tab.dataset;
      const isDateTab = DATE_TAB_RULES.has(tabType);
      const isEmptyableCategory = tabType === 'category' && 'hideWhenEmpty' in tab.dataset;
      if (!isDateTab && !isEmptyableCategory) continue;
      // A category tab appears only while one of its sales is live, as in the
      // app; upcoming sales alone do not reveal it.
      const cards = this.#getMatchingCards(tab);
      const shown = isEmptyableCategory ? cards.filter((c) => c.dataset.status === 'active') : cards;
      tab.hidden = !designMode && shown.length === 0;
    }
    this.#tabs = this.#tabs.filter((tab) => !tab.hidden);
  }

  // ── Tab activation ──

  /** @param {number} index */
  #activateTab(index) {
    if (index < 0 || index >= this.#tabs.length) return;

    const previous = this.#tabs[this.#activeTabIndex]?.dataset.tabType ?? null;
    this.#activeTabIndex = index;
    const tab = this.#tabs[index];
    const matching = this.#getMatchingCards(tab);

    for (const [i, t] of this.#tabs.entries()) {
      const isActive = i === index;
      t.setAttribute('aria-selected', String(isActive));
      t.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    // Both nav surfaces read from the same activation, so the header quick links
    // move with the tab bar instead of keeping the state the server rendered.
    this.#syncHeaderLinks();
    this.#placeIndicator();

    this.#hideAllCards();
    this.#showCards(matching, this.#pageSize);
    this.#visibleCounts.set(index, Math.min(this.#pageSize, matching.length));

    const isEmpty = matching.length === 0;
    if (this.#grid) this.#grid.hidden = isEmpty;
    if (this.#emptyState) this.#emptyState.hidden = !isEmpty;

    // Re-arm infinite scroll and refresh the CTA for the new tab
    this.#syncScroll();

    this.#announceTab(previous);
  }

  // ── Hero reveal ──

  /**
   * Whether this click needs the grid pulled into view. A hero that fills the
   * viewport sits above the grid, so without this a switch between the ongoing
   * and upcoming views changes nothing the visitor can see and the click reads
   * as broken.
   *
   * Two conditions. The homepage must be showing such a hero, read off
   * data-hero-layout, so a homepage without one never scrolls. And the visitor
   * must still be within the hero's height, measured off the hero's own bottom
   * edge: past it they chose where they are, and moving them from there is the
   * jolt this feature is meant to avoid, not cause.
   *
   * @param {number} index
   * @returns {boolean}
   */
  #shouldRevealView(index) {
    if (!this.#isHomepage || !this.#tabBar || !this.#hero) return false;
    if (!OperationGrid.#HERO_LAYOUTS.has(this.dataset.heroLayout)) return false;
    if (!OperationGrid.#REVEAL_TABS.has(this.#tabs[index]?.dataset.tabType)) return false;

    return this.#hero.getBoundingClientRect().bottom > 0;
  }

  /**
   * Scrolls the newly activated view into sight.
   *
   * Going to the upcoming view, the tab bar is the target rather than the first
   * card. It already carries scroll-margin-block-start to clear the sticky
   * header, the cards start immediately under it so nothing is scrolled past,
   * and it leaves the tabs on screen.
   *
   * Going back to the ongoing view returns to the top of the page, since the
   * hero is itself advertising an ongoing sale. Reachable only while part of
   * the hero is still on screen, so it is a short correction rather than a
   * jump back up the page.
   *
   * Reduced motion gets the same destination without the travel. Rapid
   * switching is safe: a second scroll supersedes one still running rather than
   * queueing behind it.
   *
   * @param {number} index
   */
  #revealView(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    if (this.#tabs[index]?.dataset.tabType === 'ongoing') {
      window.scrollTo({ top: 0, behavior });
    } else {
      this.#tabBar?.scrollIntoView({ behavior, block: 'start' });
    }
  }

  /**
   * Halts a smooth scroll still in flight, leaving the page where it stands.
   *
   * A click that reveals nothing must not move the page. A reveal started by
   * the previous click keeps animating towards its old target until something
   * supersedes it, which would carry the visitor past the hero after the very
   * click that was supposed to leave them alone. Scrolling to the offset the
   * page already sits at supersedes it and lands nowhere.
   */
  #stopScrolling() {
    window.scrollTo({ top: window.scrollY, behavior: 'instant' });
  }

  /**
   * Announces the active homepage tab to other scripts and mirrors it on data-active-tab,
   * so a script that starts after the event can still read the current tab.
   * @param {string | null} previous - type of the tab that was active before, if any
   */
  #announceTab(previous) {
    const tab = this.#tabs[this.#activeTabIndex]?.dataset.tabType ?? null;
    this.dataset.activeTab = tab ?? '';
    this.dispatchEvent(new CustomEvent('operation-grid:tab-change', { bubbles: true, detail: { tab, previous } }));
  }

  // ── Active-tab indicator ──

  /**
   * The tab the pill sits under: the selected tab on the homepage, the tab the
   * server marked as current on dedicated pages, which can be none of them.
   * @returns {HTMLElement|null}
   */
  #currentTab() {
    if (this.#isHomepage) return this.#tabs[this.#activeTabIndex] ?? null;
    return this.#tabs.find((tab) => tab.getAttribute('aria-current') === 'page') ?? null;
  }

  /**
   * Writes the active tab's box into the custom properties the pill reads.
   * offsetLeft/offsetTop are measured from the tab bar's padding edge, which is
   * also the pill's containing block, so the two agree even when the bar is
   * scrolled sideways.
   */
  #placeIndicator() {
    if (!this.#tabBar || !this.#indicator) return;

    const tab = this.#currentTab();
    if (!tab) {
      this.#indicator.style.setProperty('--indicator-width', '0px');
      return;
    }

    this.#indicator.style.setProperty('--indicator-x', `${tab.offsetLeft}px`);
    this.#indicator.style.setProperty('--indicator-y', `${tab.offsetTop}px`);
    this.#indicator.style.setProperty('--indicator-width', `${tab.offsetWidth}px`);
    this.#indicator.style.setProperty('--indicator-height', `${tab.offsetHeight}px`);
  }

  /**
   * First placement plus re-placement whenever the tab bar is re-laid out (font
   * swap, viewport change). [data-indicator-ready] is set a frame later so the
   * initial placement lands without sliding in, and it is what hands the pill
   * over from the CSS-only fallback.
   */
  #armIndicator() {
    if (!this.#tabBar || !this.#indicator) return;

    this.#placeIndicator();
    requestAnimationFrame(() => this.#tabBar?.setAttribute('data-indicator-ready', ''));

    // The tabs are observed alongside the bar: a font swap changes their widths
    // while the bar, once clamped at max-width, keeps the same box.
    this.#layoutObserver = new ResizeObserver(() => this.#placeIndicator());
    this.#layoutObserver.observe(this.#tabBar);
    for (const tab of this.#tabs) this.#layoutObserver.observe(tab);
  }

  /**
   * Returns cards matching a tab's filter criteria.
   * @param {HTMLElement} tab
   * @returns {HTMLElement[]}
   */
  #getMatchingCards(tab) {
    const type = tab.dataset.tabType;
    const category = tab.dataset.tabCategory;

    // Date tabs -- every active sale with a window, the hero's included
    const rule = DATE_TAB_RULES.get(type);
    if (rule) {
      const now = Date.now() / 1000;
      return this.#cards.filter(
        (c) =>
          c.dataset.status === 'active' &&
          c.dataset.startsAt !== undefined &&
          rule(Number(c.dataset.startsAt), Number(c.dataset.endsAt), now)
      );
    }

    if (type === 'upcoming') {
      return this.#cards.filter((c) => c.dataset.status === 'upcoming');
    }

    if (type === 'category' && category) {
      return this.#cards.filter((c) => {
        const cats = c.dataset.categories;
        if (!cats) return false;
        return cats.split(',').includes(category);
      });
    }

    // ongoing -- active cards only, the hero's sales left to the hero above
    return this.#cards.filter((c) => c.dataset.status === 'active' && c.dataset.hero === undefined);
  }

  #hideAllCards() {
    for (const card of this.#cards) {
      card.removeAttribute('data-visible');
    }
  }

  /**
   * @param {HTMLElement[]} cards
   * @param {number} count
   */
  #showCards(cards, count) {
    for (let i = 0; i < Math.min(count, cards.length); i++) {
      cards[i].style.setProperty('--card-index', String(i));
      cards[i].setAttribute('data-visible', '');
    }
  }

  // ── Infinite scroll ──

  /**
   * Cards in play for the current context: the active tab's set on the homepage,
   * the full server-rendered set on dedicated pages.
   * @returns {HTMLElement[]}
   */
  #currentMatching() {
    if (this.#isHomepage) {
      const tab = this.#tabs[this.#activeTabIndex];
      return tab ? this.#getMatchingCards(tab) : [];
    }
    return this.#cards;
  }

  /**
   * Batch (1-indexed) that a given visible count corresponds to.
   * @param {number} count
   * @returns {number}
   */
  #pageOf(count) {
    if (this.#pageSize <= 0) return count > 0 ? 1 : 0;
    return Math.ceil(count / this.#pageSize);
  }

  /** Reveal the next batch of cards for the current context. */
  #revealNext() {
    const matching = this.#currentMatching();
    if (matching.length === 0) return;

    const current = this.#visibleCounts.get(this.#activeTabIndex) ?? 0;
    if (current >= matching.length) return;

    const next = Math.min(current + this.#pageSize, matching.length);
    this.#showCards(matching, next);
    this.#visibleCounts.set(this.#activeTabIndex, next);
  }

  /**
   * Central state machine: decides whether the scroll observer is armed, whether
   * the grid is paused (manual button), or exhausted, and renders the CTA to match.
   */
  #syncScroll() {
    const matching = this.#currentMatching();
    const visibleCount = this.#visibleCounts.get(this.#activeTabIndex) ?? 0;
    const exhausted = visibleCount >= matching.length;
    const page = this.#pageOf(visibleCount);
    const paused =
      !exhausted && this.#pagesBeforePause > 0 && page % this.#pagesBeforePause === 0;

    if (exhausted || paused) {
      this.#disarmObserver();
    } else {
      this.#armObserver();
    }

    this.#renderCTA(exhausted, paused);
  }

  #armObserver() {
    if (!this.#sentinel) return;
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(this.#handleIntersect, {
        rootMargin: '300px 0px',
      });
    }
    // Re-observe so a sentinel that is still on-screen re-fires and chains the next
    // batch on the following frame, filling the viewport without a manual scroll.
    this.#observer.disconnect();
    this.#observer.observe(this.#sentinel);
  }

  #disarmObserver() {
    this.#observer?.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#revealNext();
    this.#syncScroll();
  };

  // ── CTA ──

  /**
   * Renders the shared CTA element into one of three states:
   *  - paused: manual load-more button (per-tab label/url on the homepage)
   *  - exhausted: hidden on the homepage, "back to home" link on dedicated pages
   *  - continuous mid-scroll: hidden
   * @param {boolean} exhausted
   * @param {boolean} paused
   */
  #renderCTA(exhausted, paused) {
    if (!this.#ctaWrapper) return;

    if (paused) {
      this.#ctaWrapper.hidden = false;
      if (this.#cta) {
        this.#cta.setAttribute('data-load-more', '');
        if (this.#isHomepage) {
          const tab = this.#tabs[this.#activeTabIndex];
          const label = tab?.dataset.tabCtaLabel;
          const url = tab?.dataset.tabCtaUrl;
          if (label) this.#cta.textContent = label;
          if (url) this.#cta.href = url;
        }
      }
      return;
    }

    if (exhausted) {
      if (this.#isHomepage) {
        this.#ctaWrapper.hidden = true;
      } else {
        // Dedicated page -- swap the button to a back-to-home link (href already root_url)
        this.#ctaWrapper.hidden = false;
        if (this.#cta) {
          this.#cta.removeAttribute('data-load-more');
          const homeLabel = this.dataset.homeCtaLabel;
          if (homeLabel) this.#cta.textContent = homeLabel;
        }
      }
      return;
    }

    // Continuous infinite scroll mid-list -- no button
    this.#ctaWrapper.hidden = true;
  }

  // ── Event handlers ──

  /** Tab types that always navigate (no client-side filter): date-based tabs and redirect links */
  static #NAV_ONLY_TABS = new Set(['new_sales', 'last_hours', 'link']);

  /** Hero layouts that fill the viewport above the grid, hiding any view change behind them. */
  static #HERO_LAYOUTS = new Set(['hero', 'hero-compact']);

  /**
   * Tab types the hero reveal covers: the two views the homepage hero sits
   * above, and the last day tab, whose sales are the ones about to leave.
   */
  static #REVEAL_TABS = new Set(['ongoing', 'upcoming', 'last_day']);

  /**
   * A click the browser should handle itself: a modified click opening the link
   * in a new tab or window, or anything but the primary button.
   * @param {MouseEvent} event
   * @returns {boolean}
   */
  static #isPlainClick(event) {
    return (
      !event.defaultPrevented &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  /** @param {MouseEvent} event */
  #handleTabClick = (event) => {
    const tab = event.target.closest('.operation-grid__tab');
    if (!tab) return;

    // Date-based tabs always navigate (even on homepage)
    if (OperationGrid.#NAV_ONLY_TABS.has(tab.dataset.tabType)) return;

    // On homepage, intercept and filter client-side
    if (this.#isHomepage && OperationGrid.#isPlainClick(event)) {
      event.preventDefault();
      const index = this.#tabs.indexOf(tab);
      if (index === -1) return;

      // Measured before the swap so the gate reflects where the visitor stood
      // when they clicked, performed after it so the scroll lands against the
      // new view's final page height.
      const reveal = this.#shouldRevealView(index);
      this.#activateTab(index);
      this.#pushTabUrl(index);
      if (reveal) this.#revealView(index);
      else this.#stopScrolling();
    }
    // On dedicated pages, let the link navigate
  };

  /** @param {KeyboardEvent} event */
  #handleTabKeydown = (event) => {
    if (!this.#isHomepage) return;

    const currentIndex = this.#tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];

    // Nav-only tabs (new_sales / last_hours / link) are links, not client-side
    // filters, so move roving focus onto them without activating/filtering
    // (mirrors #handleTabClick, which also skips NAV_ONLY_TABS). Enter/Space
    // still follows the link.
    if (OperationGrid.#NAV_ONLY_TABS.has(nextTab.dataset.tabType)) {
      for (const [i, tab] of this.#tabs.entries()) {
        tab.setAttribute('tabindex', i === nextIndex ? '0' : '-1');
      }
    } else {
      this.#activateTab(nextIndex);
    }
    nextTab.focus();
  };

  /** @param {MouseEvent} event */
  #handleLoadMore = (event) => {
    // Home-link mode (dedicated page with all cards revealed) -- let navigation happen
    if (!this.#cta?.hasAttribute('data-load-more')) return;

    event.preventDefault();

    // Reveal one batch, then re-arm scroll (continuous until the next pause boundary)
    this.#revealNext();
    this.#syncScroll();
  };

  // ── Header quick links (homepage only) ──

  /**
   * Compares two theme-rendered URLs. Both sides come out of Liquid already
   * resolved, including the locale prefix on the root, so a normalised pathname
   * comparison is enough.
   * @param {string|undefined} value
   * @returns {string|null}
   */
  static #normalizeUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value, location.origin);
      return `${url.pathname.replace(/\/+$/, '') || '/'}${url.search}`;
    } catch {
      return null;
    }
  }

  /**
   * The header exposes the same two destinations as the first tabs, as plain
   * links. On the homepage those links reload the document to show a filter the
   * page can already apply in place -- including "Ventes en cours", which
   * reloads the very page the visitor is on. Adopting them makes both nav rows
   * drive the same state, and leaves them untouched anywhere the grid cannot
   * satisfy the click (unknown destination, nav-only tab, modified click).
   */
  #adoptHeaderLinks() {
    const { signal } = this.#controller;

    for (const link of document.querySelectorAll('[data-nav-filter-url]')) {
      const target = OperationGrid.#normalizeUrl(link.dataset.navFilterUrl);
      if (!target) continue;

      const index = this.#tabs.findIndex(
        (tab) => OperationGrid.#normalizeUrl(tab.dataset.tabCtaUrl) === target
      );
      if (index === -1) continue;
      if (OperationGrid.#NAV_ONLY_TABS.has(this.#tabs[index].dataset.tabType)) continue;

      link.addEventListener('click', this.#handleHeaderLinkClick, { signal });
      this.#headerLinks.push({ link, index });
    }

    this.#syncHeaderLinks();
  }

  /** @param {MouseEvent} event */
  #handleHeaderLinkClick = (event) => {
    if (!OperationGrid.#isPlainClick(event)) return;

    const entry = this.#headerLinks.find(({ link }) => link === event.currentTarget);
    if (!entry) return;

    event.preventDefault();

    // Under a full-screen hero the tab bar sits below the fold, so these header
    // links are the only view control the visitor can reach and the reveal has
    // to hang off them too. Measured before the swap, run after it, as on the
    // tab bar itself.
    const reveal = this.#shouldRevealView(entry.index);
    this.#activateTab(entry.index);
    this.#pushTabUrl(entry.index);

    if (reveal) {
      this.#revealView(entry.index);
    } else if (this.#tabBar && this.#tabBar.getBoundingClientRect().top < 0) {
      // The header is always in reach, the grid is not, so a visitor who has
      // scrolled past the tab bar comes back to it instead of being left near
      // the footer when the new view is shorter.
      this.#tabBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      this.#stopScrolling();
    }
  };

  /** Mirrors the active tab onto the adopted header links. */
  #syncHeaderLinks() {
    for (const { link, index } of this.#headerLinks) {
      if (index === this.#activeTabIndex) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
  }

  // ── History ──

  /**
   * Keeps the URL in step with the two tabs the header also exposes, so Back
   * returns to the previous filter instead of leaving the page -- which is what
   * it did while those links were real navigations. Category tabs keep their
   * existing behaviour and write nothing to history.
   * @param {number} index
   */
  #pushTabUrl(index) {
    const type = this.#tabs[index]?.dataset.tabType;
    if (type !== 'ongoing' && type !== 'upcoming') return;

    const base = `${location.pathname}${location.search}`;
    const next = type === 'upcoming' ? `${base}#${UPCOMING_HASH}` : base;
    if (next === `${base}${location.hash}`) return;

    history.pushState(null, '', next);
  }

  #handlePopState = () => {
    const type = HASH_ALIASES.includes(location.hash.replace('#', '')) ? 'upcoming' : 'ongoing';
    const index = this.#tabs.findIndex((tab) => tab.dataset.tabType === type);
    if (index !== -1 && index !== this.#activeTabIndex) this.#activateTab(index);
  };

  // ── Hash navigation ──

  #handleHashNavigation() {
    const hash = location.hash.replace('#', '');
    if (!hash || !HASH_ALIASES.includes(hash)) return;

    const target = this.querySelector('#a-venir');
    if (!target) return;

    // On homepage, the target lives under the upcoming tab -- activate it first
    if (this.#isHomepage) {
      const upcomingIndex = this.#tabs.findIndex((t) => t.dataset.tabType === 'upcoming');
      if (upcomingIndex !== -1 && upcomingIndex !== this.#activeTabIndex) {
        this.#activateTab(upcomingIndex);
      }
    }

    // Homepage filters per active tab; dedicated pages use the already-filtered server-rendered set
    const tab = this.#tabs[this.#activeTabIndex];
    const matching = this.#isHomepage && tab ? this.#getMatchingCards(tab) : this.#cards;
    const targetIndex = matching.indexOf(target);
    if (targetIndex === -1) return;

    const needed = targetIndex + 1;
    const currentCount = this.#visibleCounts.get(this.#activeTabIndex) ?? 0;

    if (needed > currentCount) {
      const batchesNeeded = Math.ceil(needed / this.#pageSize);
      const revealCount = Math.min(batchesNeeded * this.#pageSize, matching.length);

      this.#showCards(matching, revealCount);
      this.#visibleCounts.set(this.#activeTabIndex, revealCount);

      // Re-sync scroll/CTA state around the newly revealed batches
      this.#syncScroll();
    }

    // Scroll to the target after a frame to ensure layout has updated.
    // Reveal above happens synchronously, so the anchor scroll always fires
    // regardless of the infinite-scroll pause state.
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
}

customElements.define('operation-grid', OperationGrid);
